import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { ReactNode } from "react";

import { useCountries } from "../../../catalogs/countries";
import { ChoosingModal } from "../../../components/ui-kit/choosing-modal/choosing-modal";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { SelectionMode } from "../../../models/data-model";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { Clinics } from "../clinics/clinics";

export const ClinicsModal = observer(function ClinicsModal(props: {
  model: Clinics;
  mode: SelectionMode;
  preselected?: string[];
  filter?: Record<string, any[]>;
  title: ReactNode;
  onConfirm: (params: { selected: any[] }) => void;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const countries = useCountries();
  const initialColumnFilters = props.filter?.country?.length
    ? [{ id: "country", value: props.filter.country }]
    : [];

  return (
    <ChoosingModal
      model={props.model}
      onClose={props.onClose}
      onConfirm={props.onConfirm}
      title={props.title}
      fillHeight
      table={
        <DataTable
          model={props.model}
          itemKeyField="uuid"
          isLoading={props.model.loading}
          fillHeight
          canSelect={props.mode}
          preselected={props.preselected}
          filterNoun={(count) =>
            t`${plural(count, { one: "clinic", other: "clinics" })}`
          }
          initialColumnFilters={initialColumnFilters}
          columns={[
            {
              header: <Trans>Name</Trans>,
              attr: "name",
              type: "string",
              sort: true,
              initSort: "asc",
              align: "left",
            },
            {
              header: <Trans>Ambulance</Trans>,
              attr: "ambulance",
              type: "string",
              align: "left",
            },
            {
              header: <Trans>Country</Trans>,
              attr: "country",
              type: "string",
              align: "left",
              filter: {
                kind: "multi",
                group: t`Location`,
                options: getCountryOptions,
              },
            },
            {
              header: <Trans>Region</Trans>,
              attr: "region",
              align: "left",
              filter: {
                kind: "multi",
                group: t`Location`,
                options: (table) => getRegionOptions(table, countries),
                disabled: regionDisabled,
                disabledMessage: t`Select a country first`,
                predicate: regionPredicate,
              },
              cell: ({ item }) =>
                item.region != null
                  ? countries.getRegionSchema(item.country, item.region)
                      ?.region_name
                  : undefined,
            },
            {
              header: <Trans># of physicians</Trans>,
              width: 30,
              cell: ({ item }) => item.numPhysicians,
            },
          ]}
        />
      }
    />
  );
});
